import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter, OnDestroy, OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { AMPLITUDE_EVENT_CATEGORIES, AMPLITUDE_EVENTS, AmplitudeService } from 'app/global-services/amplitude.service';
import { Router } from '@angular/router';
import { VideoStreamService } from 'app/global-services/video-stream.service';
import { from } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';

enum LEAD_CAPTURE_SCREEN {
    CAPTURE = 'capture',
    PARSING = 'parsing',
    SUCCESS = 'success',
    ERROR = 'error',
}

enum WEBCAM_FACING_MODE {
    FRONT = 'front',
    BACK = 'back',
}

@Component({
    selector: 'app-image-capture',
    templateUrl: './image-capture.component.html',
    styleUrls: ['./image-capture.component.scss'],
})
export class ImageCaptureComponent implements OnInit, OnDestroy, AfterViewInit {

    @Output() closeScreen = new EventEmitter();
    @Output() onBackBtnEmitter = new EventEmitter();
    @Output() summarizeDataEmitter = new EventEmitter();
    @Output() onScanErrorEmitter = new EventEmitter();
    @ViewChild('videoElement', { static: false }) videoElement: ElementRef;
    @ViewChild('imageCaptureAudio', { static: false }) audioElement: ElementRef;
    @ViewChild('canvas', { static: false }) canvasElement: ElementRef;

    isMobileDevice = true;
    isiOS = false;

    LEAD_CAPTURE_SCREEN = LEAD_CAPTURE_SCREEN;

    capturedImage: File | null = null;
    tempCaptureImage: string | ArrayBuffer | null = '';

    currentLeadCaptureScreen: LEAD_CAPTURE_SCREEN = LEAD_CAPTURE_SCREEN.CAPTURE;
    webcamFacingMode: WEBCAM_FACING_MODE = WEBCAM_FACING_MODE.BACK;

    // State -> Loading, Camera Error, Flashlight
    loadingCamera: boolean = true;
    errorCamera: boolean = false;
    flashLight: boolean = false;

    constructor(
        private readonly videoStreamService: VideoStreamService,
        private readonly router: Router,
        private readonly deviceDetectorService: DeviceDetectorService,
        private readonly amplitudeService: AmplitudeService
    ) {}

    // Check if the device is mobile or not and if it is iOS
    ngOnInit() {
        this.isMobileDevice = this.deviceDetectorService.isMobile();
        this.isiOS = this.deviceDetectorService.os === 'iOS';
    }

    ngAfterViewInit() {
        if (this.videoElement) {
            this.loadingCamera = true;
            if (this.isMobileDevice) {
                this.switchWebcamFacingMode(WEBCAM_FACING_MODE.BACK)
            } else {
                this.switchWebcamFacingMode(WEBCAM_FACING_MODE.FRONT);
            }
        }

        this.addEnterEventListeners();
    }

    ngOnDestroy() {
        this.videoStreamService.stopWebcam();
    }

    handleCloseScreen() {
        this.router.navigate(['/cards'], {
            skipLocationChange: false,
        });
    }

    // Handle image click from webcam
    captureImage() {
        const video = this.videoElement.nativeElement;
        const canvas = this.canvasElement.nativeElement;

        // Set canvas size to match video dimensions
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const context = canvas.getContext('2d');
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert the canvas to an image data URL (PNG format)
        const imageDataUrl = canvas.toDataURL('image/png');
        const byteString = atob(imageDataUrl.split(',')[1]);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const uint8Array = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteString.length; i++) {
            uint8Array[i] = byteString.charCodeAt(i);
        }

        // Create a Blob from the ArrayBuffer
        const blob = new Blob([uint8Array], { type: 'image/jpeg' });

        // Create a File from the Blob
        const file = new File([blob], 'captured-image.jpeg', {
            type: 'image/jpeg',
        });
        this.capturedImage = file;
        this.tempCaptureImage = URL.createObjectURL(file);
        this.switchLeadCaptureScreen(LEAD_CAPTURE_SCREEN.PARSING);
        this.showCaptureAnimation();
        this.amplitudeService.logEvent(AMPLITUDE_EVENT_CATEGORIES.Onboarding, AMPLITUDE_EVENTS.SCAN_FROM_PHOTO, { scan_method: 'camera' });
    }

    // Handle image upload from storage i.e on left of capture button
    handleUploadImageFromStorage(e) {
        const uploadedFile = e.target.files[0];
        this.capturedImage = uploadedFile;
        this.updateLeadImage(uploadedFile);
        this.switchLeadCaptureScreen(LEAD_CAPTURE_SCREEN.PARSING);
        this.showCaptureAnimation();
        this.amplitudeService.logEvent(AMPLITUDE_EVENT_CATEGORIES.Onboarding, AMPLITUDE_EVENTS.SCAN_FROM_PHOTO, { scan_method: 'upload_photo_from_file' });
    }

    closeImageCaptureScreen(event: 'closeScreen' | 'retakeImage') {
        if (event === 'closeScreen') {
            this.videoStreamService.stopWebcam();
            this.closeScreen.emit();
        } else {
            this.currentLeadCaptureScreen = LEAD_CAPTURE_SCREEN.CAPTURE
            this.loadingCamera = true;
            // Reset captured images
            setTimeout(() => {
                if (this.videoElement) {
                    if (this.isMobileDevice) {
                        this.switchWebcamFacingMode(WEBCAM_FACING_MODE.BACK)
                    } else {
                        this.switchWebcamFacingMode(WEBCAM_FACING_MODE.FRONT);
                    }
                }
            });
        }
    }

    handleDesktopBackCta() {
        this.closeImageCaptureScreen('closeScreen');
        this.onBackBtnEmitter.emit();
    }

    onSummarizeDataRecive(event) {
        this.amplitudeService.logEvent(AMPLITUDE_EVENT_CATEGORIES.Onboarding, AMPLITUDE_EVENTS.PHOTO_IMAGE_PROCESSED, { status: 'successful' });
        this.summarizeDataEmitter.emit(event)
        this.switchLeadCaptureScreen(LEAD_CAPTURE_SCREEN.CAPTURE);
    }

    onScanError() {
        this.onScanErrorEmitter.emit();
        this.amplitudeService.logEvent(AMPLITUDE_EVENT_CATEGORIES.Onboarding, AMPLITUDE_EVENTS.PHOTO_IMAGE_PROCESSED, { status: 'failed' });
    }

    // Handling Flash light toogle state
    handleFlashToggle() {
        from(this.videoStreamService.toggleFlashlight(!this.flashLight)).subscribe({
            next: (resFlashStatus) => {
                this.flashLight = resFlashStatus;
            },
            error: (err) => {
                console.error('Error toggling flashlight:', err);
            }
        });
    }

    private switchLeadCaptureScreen(screen: LEAD_CAPTURE_SCREEN) {
        this.currentLeadCaptureScreen = screen;
    }

    addEnterEventListeners() {
        document.addEventListener('keydown', (event) => {
            if (this.currentLeadCaptureScreen === LEAD_CAPTURE_SCREEN.CAPTURE && event.key === 'Enter') {
                this.captureImage();
            }
        });
    }

    private switchWebcamFacingMode(position: WEBCAM_FACING_MODE) {
        this.webcamFacingMode = position;
        this.initializeWebcam(position);
    }

    private handleWebcamError(err: any) {
        this.handleCameraError(err);
        console.error('Error accessing webcam:', err);
    }

    private handleCameraError(err: any) {
        console.error('Error initializing camera:', err);
        if (err.name === 'NotAllowedError') {
            this.errorCamera = true;
            console.error('Camera access denied. Please allow access in your browser settings and refresh.');
        }
        if (err.name === 'OverconstrainedError') {
            this.errorCamera = true;
            console.error('Requested camera type is not available. Using default camera.');
        }
    }

    // Store image in temp variable for preview
    private updateLeadImage(passedLeadImage: File) {
        const reader = new FileReader();
        reader.onload = () => {
            this.tempCaptureImage = reader.result;
        };
        reader.readAsDataURL(passedLeadImage);
    }

    private initializeWebcam(cameraType: WEBCAM_FACING_MODE = WEBCAM_FACING_MODE.BACK) {
        this.videoStreamService.startWebcam(cameraType)
            .then(() => {
                this.loadingCamera = false;
                this.errorCamera = false;
                this.videoStreamService.setVideoStream(this.videoElement);
            })
            .catch((err) => {
                this.loadingCamera = true;
                this.switchLeadCaptureScreen(LEAD_CAPTURE_SCREEN.ERROR);
                this.handleWebcamError(err);
            });
    }

    captureDesktopImage() {
        document.getElementById('file-upload-desktop').click();
    }

    private showCaptureAnimation() {
        setTimeout(() => {
            this.videoStreamService.stopWebcam();
            this.switchLeadCaptureScreen(LEAD_CAPTURE_SCREEN.SUCCESS);
        }, 1000);
    }
}
